import { Translation } from '@ps/web-shared-components';
import { isToday, isTomorrow } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import React from 'react';

import type { Props as FormattedTimeZoneProps } from '@swf/components/FormattedTimeZone/FormattedTimeZone';
import FormattedTimeZone from '@swf/components/FormattedTimeZone/FormattedTimeZone';
import useTimeZone from '@swf/core/hooks/useTimeZone';
import { useDisplayConfig } from '@swf/sports/providers/DisplayConfigProvider';

import { TXTWCL_Today, TXTWCL_Tomorrow } from './FormattedDateTime.messages.json';

interface LongDateTimeOptions {
  format: 'long';
  weekday?: Extract<Intl.DateTimeFormatOptions['weekday'], 'short' | 'long'>;
  hideTime?: true;
  /**
   * shortens the output when the provided date is today or tomorrow by hiding the date part
   * @example Today, 12:00
   * @example Tomorrow, 12:00
   * @example Sunday 1 Jan 1970 at 12:00
   */

  minimal?: true;
}

interface ShortDateTimeOptions {
  format: 'short';
  weekday?: never;
  hideTime?: true;
  minimal?: never;
}

interface TimeOptions {
  format?: 'time';
  weekday?: never;
  hideTime?: never;
  minimal?: never;
}

export type DateTimeOptions = LongDateTimeOptions | ShortDateTimeOptions | TimeOptions;

export type Props = DateTimeOptions & {
  date: FormattedTimeZoneProps['date'];
};

const timeOptions = {
  hourCycle: 'h23',
  hour: 'numeric',
  minute: 'numeric'
} satisfies Intl.DateTimeFormatOptions;

const FormattedDateTime: React.FC<Props> = ({
  date,
  hideTime,
  minimal,
  format = 'time',
  weekday = 'short'
}) => {
  const { dateTimeOptions } = useDisplayConfig();
  const { timeZoneName } = useTimeZone();

  const derivedMinimal = dateTimeOptions?.minimal ?? minimal;
  const derivedFormat = dateTimeOptions?.format ?? format;
  const derivedWeekday = dateTimeOptions?.weekday ?? weekday;

  const zonedDate = utcToZonedTime(date, timeZoneName);
  const isDateToday = isToday(zonedDate);
  const isDateTomorrow = isTomorrow(zonedDate);

  const shouldShowWeekday = derivedFormat === 'long' && !isDateToday && !isDateTomorrow;

  const dateOptions = {
    year: 'numeric',
    day: 'numeric',
    ...(shouldShowWeekday && { weekday: derivedWeekday }),
    ...(derivedFormat !== 'time' && { month: derivedFormat }),
    ...(!hideTime && timeOptions)
  } satisfies Intl.DateTimeFormatOptions;

  switch (derivedFormat) {
    case 'time':
      return <FormattedTimeZone date={date} {...timeOptions} />;

    case 'short':
      return <FormattedTimeZone date={date} {...dateOptions} />;

    case 'long': {
      const { year, month, day, ...rest } = dateOptions;
      return (
        <>
          {isDateToday && <Translation {...TXTWCL_Today} />}
          {isDateTomorrow && <Translation {...TXTWCL_Tomorrow} />}
          {(isDateToday || isDateTomorrow) && ',\u00A0'}
          <FormattedTimeZone
            date={date}
            {...(derivedMinimal && (isDateToday || isDateTomorrow) ? rest : dateOptions)}
          />
        </>
      );
    }
  }
};

export default FormattedDateTime;
